import {ActivatedRouteSnapshot, RouterStateSnapshot, Routes} from '@angular/router';
import {EventsService} from '../../services/events/events.service';
import {EventInquiriesService} from '../../services/event-inquiries/event-inquiries.service';
import {EventActivityService} from '../../services/activities/event-activity.service';
import {EventOverviewSnapshotService} from '../../services/event-overview-snapshot/event-overview-snapshot.service';
import {EventCalendarByGroupService} from '../../services/event-calendar/event-calendar-by-group.service';
import {EventInquiryVariantsService} from '../../services/event-inquiry-variants/event-inquiry-variants.service';
import {EventDetailShellComponent} from './event-detail-shell/event-detail-shell.component';
import {EventInquiryDetailShellComponent} from './event-inquiry-detail-shell/event-inquiry-detail-shell.component';
import {EventOverviewSnapshotDetailShellComponent, requireSnapshotInquiry} from './event-overview-snapshot-detail-shell/event-overview-snapshot-detail-shell.component';
import {resolveEventOverviewSnapshot, resolveEventOverviewSnapshotBreadcrumb, resolveEventOverviewSnapshotInquiry} from './event-overview-snapshot.resolvers';
import {AuthenticatedBaseComponent} from '../authenticated-base.component';
import {CanActivateGuard} from '../../guards/can-activate.guard';
import {PERMISSION} from '@generated-backend-types';

const canDeactivateFn = (
  component: AuthenticatedBaseComponent,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState: RouterStateSnapshot,
) => component.canDeactivate(currentRoute, currentState, nextState);

const EVENTS_BREADCRUMB = {label: 'events.breadcrumb', translate: true, route: '/events'};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./events-index/events-index.component').then(m => m.EventsIndexComponent),
    canActivate: [CanActivateGuard],
    data: {breadcrumb: [EVENTS_BREADCRUMB], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_EVENT_READ_ALL]},
  },
  {
    path: ':id',
    component: EventDetailShellComponent,
    resolve: {event: EventsService},
    canActivate: [CanActivateGuard],
    providers: [
      EventInquiriesService,
      EventActivityService,
      EventOverviewSnapshotService,
      EventCalendarByGroupService,
    ],
    data: {
      breadcrumb: [
        EVENTS_BREADCRUMB,
        {dynamic: true, dataKey: 'event.title', route: '/events/:id'},
      ],
      allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_EVENT_READ_ALL],
    },
    children: [
      {path: '', pathMatch: 'full', redirectTo: 'details'},
      {
        path: 'details',
        loadComponent: () => import('./event-details-tab/event-details-tab.component').then(m => m.EventDetailsTabComponent),
        data: {breadcrumb: [{label: 'events.detail.tabs.details', translate: true}]},
      },
      {
        path: 'inquiries',
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./event-inquiries-index/event-inquiries-index.component').then(m => m.EventInquiriesIndexComponent),
            canActivate: [CanActivateGuard],
            data: {breadcrumb: [{label: 'events.detail.tabs.inquiries', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_RESOURCES_SERVICEPROVIDER_SERVICEPROVIDERINQUIRY_READ_ALL]},
          },
          {
            path: ':inquiryId',
            component: EventInquiryDetailShellComponent,
            resolve: {inquiry: EventInquiriesService},
            canActivate: [CanActivateGuard],
            providers: [EventInquiryVariantsService],
            data: {
              breadcrumb: [
                {dynamic: true, dataKey: 'inquiry.ext_inquiry_id', route: '/events/:id/inquiries/:inquiryId'},
              ],
              allPermissionsNeeded: [PERMISSION.SPH_RESOURCES_SERVICEPROVIDER_SERVICEPROVIDERINQUIRY_READ_ALL],
            },
            children: [
              {path: '', pathMatch: 'full', redirectTo: 'offer'},
              {
                path: 'event-overview',
                data: {breadcrumb: [{label: 'events.detail.inquiry.tabs.event_overview', translate: true}]},
                children: [
                  {
                    path: '**',
                    loadComponent: () => import('./event-inquiry-overview-tab/event-inquiry-overview-tab.component').then(m => m.EventInquiryOverviewTabComponent),
                    canActivate: [CanActivateGuard],
                    data: {breadcrumb: [], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_OVERVIEW_EVENTOVERVIEWSNAPSHOT_READ_ALL]},
                  },
                ],
              },
              {
                path: 'offer',
                loadComponent: () => import('./event-inquiry-offer-tab/event-inquiry-offer-tab.component').then(m => m.EventInquiryOfferTabComponent),
                canActivate: [CanActivateGuard],
                canDeactivate: [canDeactivateFn],
                data: {breadcrumb: [{label: 'events.detail.inquiry.tabs.offer', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_RESOURCES_SERVICEPROVIDER_SERVICEPROVIDERINQUIRY_READ_ALL]},
              },
            ],
          },
        ],
      },
      {
        path: 'event-overview-snapshots',
        data: {breadcrumb: [{label: 'events.detail.tabs.event_overview_snapshots', translate: true, route: '/events/:id/event-overview-snapshots'}]},
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./event-overview-snapshots-index/event-overview-snapshots-index.component').then(m => m.EventOverviewSnapshotsIndexComponent),
            canActivate: [CanActivateGuard],
            data: {breadcrumb: [], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_OVERVIEW_EVENTOVERVIEWSNAPSHOT_READ_ALL]},
          },
          {
            path: ':snapshotId',
            component: EventOverviewSnapshotDetailShellComponent,
            resolve: {
              snapshot: resolveEventOverviewSnapshot,
              inquiry: resolveEventOverviewSnapshotInquiry,
              snapshotBreadcrumb: resolveEventOverviewSnapshotBreadcrumb,
            },
            canActivate: [CanActivateGuard],
            providers: [EventInquiryVariantsService],
            data: {
              breadcrumb: [
                {dynamic: true, dataKey: 'snapshotBreadcrumb', route: '/events/:id/event-overview-snapshots/:snapshotId'},
              ],
              allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_OVERVIEW_EVENTOVERVIEWSNAPSHOT_READ_ALL],
            },
            children: [
              {path: '', pathMatch: 'full', redirectTo: 'event-overview'},
              {
                path: 'event-overview',
                data: {breadcrumb: [{label: 'events.detail.inquiry.tabs.event_overview', translate: true}]},
                children: [
                  {
                    path: '**',
                    loadComponent: () => import('./event-inquiry-overview-tab/event-inquiry-overview-tab.component').then(m => m.EventInquiryOverviewTabComponent),
                    canActivate: [CanActivateGuard],
                    data: {breadcrumb: [], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_OVERVIEW_EVENTOVERVIEWSNAPSHOT_READ_ALL]},
                  },
                ],
              },
              {
                path: 'offer',
                loadComponent: () => import('./event-inquiry-offer-tab/event-inquiry-offer-tab.component').then(m => m.EventInquiryOfferTabComponent),
                canActivate: [CanActivateGuard],
                resolve: {requiredInquiry: requireSnapshotInquiry},
                canDeactivate: [canDeactivateFn],
                data: {breadcrumb: [{label: 'events.detail.inquiry.tabs.offer', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_RESOURCES_SERVICEPROVIDER_SERVICEPROVIDERINQUIRY_READ_ALL]},
              },
            ],
          },
        ],
      },
      {
        path: 'procedure',
        loadComponent: () => import('./event-procedure/event-procedure.component').then(m => m.EventProcedureComponent),
        canActivate: [CanActivateGuard],
        data: {breadcrumb: [{label: 'events.detail.tabs.procedure', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_SCHEDULES_ACTIVITYVIEW_READ_ALL]},
      },
      {
        path: 'timetable',
        children: [
          {
            path: 'day',
            loadComponent: () => import('./event-timetable/event-timetable.component').then(m => m.EventTimetableComponent),
            canActivate: [CanActivateGuard],
            data: {breadcrumb: [{label: 'events.detail.tabs.timetable', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_SCHEDULES_ACTIVITYVIEW_READ_ALL]},
          },
          {
            path: 'week',
            loadComponent: () => import('./event-timetable/event-timetable.component').then(m => m.EventTimetableComponent),
            canActivate: [CanActivateGuard],
            data: {breadcrumb: [{label: 'events.detail.tabs.timetable', translate: true}], allPermissionsNeeded: [PERMISSION.SPH_EVENTMANAGEMENT_EVENTS_SCHEDULES_ACTIVITYVIEW_READ_ALL]},
          },
          {path: '**', redirectTo: 'day'},
        ],
      },
    ],
  },
];
